import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// ---------- PRICING ----------
// USD per token. Source: platform.openai.com/docs/pricing.
// If a model shows up in openai_metrics but is missing here, its calls are
// NOT silently reported as $0 anymore — they're flagged via `unpricedModels`
// below so the gap is visible instead of quietly under-counting spend.
// Keep this in sync with app/utils/common.py MODEL_ROUTER_V2_DEFAULTS.
const MODEL_PRICES = {
  "gpt-5.4": { input: 2.5e-6, output: 15e-6 },
  "gpt-5.4-mini": { input: 0.75e-6, output: 4.5e-6 },
  "gpt-5.4-nano": { input: 0.2e-6, output: 1.25e-6 },
  "gpt-5.1": { input: 1.25e-6, output: 10e-6 },
  "gpt-5": { input: 1.25e-6, output: 10e-6 },
  "gpt-5-mini": { input: 0.25e-6, output: 2e-6 },
  "gpt-5-nano": { input: 0.05e-6, output: 0.4e-6 },
  "gpt-4o": { input: 2.5e-6, output: 10e-6 },
  "gpt-4.1": { input: 2e-6, output: 8e-6 },
  "gpt-4.1-mini": { input: 0.4e-6, output: 1.6e-6 },
  "gpt-4o-mini": { input: 0.15e-6, output: 0.6e-6 },
  "text-embedding-3-small": { input: 0.02e-6, output: 0 },
  "text-embedding-3-large": { input: 0.13e-6, output: 0 },
};

const isModelPriced = (model) => Boolean(MODEL_PRICES[model]);

// ---------- CALL TYPE TAXONOMY ----------
// Mirrors the call_type values written by app/utils/common.py's
// log_openai_usage() and the handful of direct openai_metrics inserts
// (the main "generation" call in ical/plato/google_handler).
const CALL_TYPE_META = {
  generation: { label: "AI Reply Generation", group: "AI Reply Generation" },
  classification: { label: "Intent Classification", group: "Message Processing" },
  language_detection: { label: "Language Detection", group: "Message Processing" },
  translation: { label: "Translation", group: "Message Processing" },
  date_extraction: { label: "Date/Time Extraction", group: "Message Processing" },
  training_pair_classification: { label: "Training Pair Classification", group: "Message Processing" },
  rag_query_rewrite: { label: "RAG Query Rewrite", group: "RAG & Knowledge Retrieval" },
  rag_embedding: { label: "RAG Query Embedding", group: "RAG & Knowledge Retrieval" },
  knowledge_embedding: { label: "Knowledge Base Embedding", group: "RAG & Knowledge Retrieval" },
  knowledge_metadata: { label: "Knowledge Base Metadata", group: "RAG & Knowledge Retrieval" },
  validated_example_embedding: { label: "Validated Example Embedding", group: "RAG & Knowledge Retrieval" },
  validated_example_search: { label: "Validated Example Search", group: "RAG & Knowledge Retrieval" },
  experience_intelligence: { label: "Experience Intelligence Snapshot", group: "Clinic Intelligence & Digests" },
  reopening_digest: { label: "Reopening Digest", group: "Clinic Intelligence & Digests" },
  quick_message_discovery: { label: "Quick Message Discovery", group: "Clinic Intelligence & Digests" },
  demo_generation: { label: "Demo Reply", group: "Demo / Sandbox" },
  demo_language_detection: { label: "Demo Language Detection", group: "Demo / Sandbox" },
};
const GROUP_ORDER = [
  "AI Reply Generation",
  "Message Processing",
  "RAG & Knowledge Retrieval",
  "Clinic Intelligence & Digests",
  "Demo / Sandbox",
];
const callTypeMeta = (callType) =>
  CALL_TYPE_META[callType] || { label: callType, group: "Other" };

const openaiCostUSD = (model, p, c) => {
  const m = MODEL_PRICES[model];
  if (!m) return 0;
  return (p || 0) * m.input + (c || 0) * m.output;
};

const twilioCostUSD = (row) => (row.is_template ? 0.03 : 0.005);

// ---------- FX ----------
const FALLBACK_FX = 1.35;

// ---------- Date helpers ----------
const monthKey = (ts) => ts.slice(0, 7);
const formatSG = (ts) =>
  new Date(ts).toLocaleString("en-SG", {
    timeZone: "Asia/Singapore",
    hour12: false,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

// ---------- Constants ----------
const LOOKBACK_OPTIONS = [
  { value: 3, label: "Last 3 months" },
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
  { value: 24, label: "Last 24 months" },
];
// Safety net so a busy period can never silently truncate to a fixed,
// unlabeled row count the way the old .limit(500)/.limit(5000) did.
// If a fetch actually hits this cap, a banner says so instead of the
// charts quietly going stale.
const FETCH_SAFETY_LIMIT = 20000;
const TABLE_ROW_LIMIT = 200;
const MAX_DELTA_MS = 10000;

export default function TokenUsage() {
  const [metrics, setMetrics] = useState([]);
  const [conv, setConv] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("all");
  const [fx, setFx] = useState(FALLBACK_FX);
  const [fxIsLive, setFxIsLive] = useState(false);
  const [monthFilter, setMonthFilter] = useState("all");
  const [lookbackMonths, setLookbackMonths] = useState(6);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState({ metrics: false, conv: false });

  useEffect(() => {
    let cancelled = false;
    fetch("https://api.exchangerate.host/latest?base=USD&symbols=SGD")
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const rate = j?.rates?.SGD;
        if (rate) {
          setFx(rate);
          setFxIsLive(true);
        }
      })
      .catch(() => {
        // keep FALLBACK_FX, fxIsLive stays false so the UI can say so
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - lookbackMonths);
    const cutoffIso = cutoff.toISOString();

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      const [{ data: m }, { data: c }, { data: cl }] = await Promise.all([
        supabase
          .from("openai_metrics")
          .select("*")
          .gte("created_at", cutoffIso)
          .order("created_at", { ascending: false })
          .limit(FETCH_SAFETY_LIMIT),
        supabase
          .from("conversations")
          .select("*")
          .gte("created_at", cutoffIso)
          .order("created_at", { ascending: false })
          .limit(FETCH_SAFETY_LIMIT),
        supabase.from("clients").select("id, raison_sociale"),
      ]);

      if (cancelled) return;

      setMetrics(m || []);
      setConv(
        (c || [])
          .slice()
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      );
      setClients(cl || []);
      setTruncated({
        metrics: (m || []).length >= FETCH_SAFETY_LIMIT,
        conv: (c || []).length >= FETCH_SAFETY_LIMIT,
      });
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [lookbackMonths]);

  const practitionerList = clients
    .filter((c) => metrics.some((m) => m.praticien_id === c.id))
    .map((c) => ({ id: c.id, label: c.raison_sociale }));

  const filteredMetrics =
    selectedDoctor === "all"
      ? metrics
      : metrics.filter((m) => m.praticien_id === selectedDoctor);

  const filteredConv =
    selectedDoctor === "all"
      ? conv
      : conv.filter((c) => c.praticien_id === selectedDoctor);

  // ---------- MODELS SEEN BUT NOT IN MODEL_PRICES ----------
  const unpricedModels = useMemo(() => {
    const counts = {};
    for (const m of filteredMetrics) {
      if (!isModelPriced(m.model)) {
        counts[m.model] = (counts[m.model] || 0) + 1;
      }
    }
    return counts;
  }, [filteredMetrics]);

  // ---------- MONTHLY TOTAL COST ----------
  const monthly = useMemo(() => {
    const acc = {};

    for (const row of filteredConv) {
      const m = monthKey(row.created_at);
      if (!acc[m]) acc[m] = { month: m, twilio: 0, openai: 0, total: 0 };
      acc[m].twilio += twilioCostUSD(row) * fx;
    }

    for (const r of filteredMetrics) {
      const m = monthKey(r.created_at);
      if (!acc[m]) acc[m] = { month: m, twilio: 0, openai: 0, total: 0 };
      acc[m].openai +=
        openaiCostUSD(r.model, r.prompt_tokens, r.completion_tokens) * fx;
    }

    return Object.values(acc)
      .map((d) => ({
        ...d,
        total: d.twilio + d.openai,
      }))
      .sort((a, b) => (a.month < b.month ? -1 : 1));
  }, [filteredConv, filteredMetrics, fx]);

  // ---------- OPENAI COST BY MODEL (current lookback window) ----------
  const costByModel = useMemo(() => {
    const acc = {};
    for (const m of filteredMetrics) {
      const cost =
        openaiCostUSD(m.model, m.prompt_tokens, m.completion_tokens) * fx;
      acc[m.model] = (acc[m.model] || 0) + cost;
    }
    return acc;
  }, [filteredMetrics, fx]);

  // ---------- CONSUMPTION BY CALL TYPE / CATEGORY ----------
  const costByCallType = useMemo(() => {
    const acc = {};
    for (const m of filteredMetrics) {
      const ct = m.call_type || "generation";
      if (!acc[ct]) acc[ct] = { call_type: ct, calls: 0, tokens: 0, cost: 0 };
      acc[ct].calls += 1;
      acc[ct].tokens += m.total_tokens || 0;
      acc[ct].cost +=
        openaiCostUSD(m.model, m.prompt_tokens, m.completion_tokens) * fx;
    }
    return Object.values(acc).sort((a, b) => b.cost - a.cost);
  }, [filteredMetrics, fx]);

  const costByGroup = useMemo(() => {
    const acc = {};
    for (const row of costByCallType) {
      const group = callTypeMeta(row.call_type).group;
      if (!acc[group]) acc[group] = { group, calls: 0, tokens: 0, cost: 0 };
      acc[group].calls += row.calls;
      acc[group].tokens += row.tokens;
      acc[group].cost += row.cost;
    }
    return GROUP_ORDER.map((g) => acc[g] || { group: g, calls: 0, tokens: 0, cost: 0 }).filter(
      (g) => g.calls > 0 || g.group === "AI Reply Generation"
    );
  }, [costByCallType]);

  const totalOpenAICostSgd = costByCallType.reduce((s, r) => s + r.cost, 0);

  const generationMetrics = filteredMetrics.filter(
    (m) => (m.call_type || "generation") === "generation"
  );
  const otherMetrics = filteredMetrics.filter(
    (m) => (m.call_type || "generation") !== "generation"
  );
  const recentOtherCalls = otherMetrics.slice(0, TABLE_ROW_LIMIT);

  // ---------- MATCH TWILIO COST PER GPT CALL (approximate) ----------
  const convByUser = useMemo(() => {
    const map = new Map();
    for (const c of filteredConv) {
      if (!map.has(c.utilisateur)) map.set(c.utilisateur, []);
      map.get(c.utilisateur).push(c);
    }
    return map;
  }, [filteredConv]);

  const associateTwilioToOpenAI = (m) => {
    const ts = new Date(m.created_at).getTime();
    const rows = convByUser.get(m.utilisateur) || [];
    let twilio = 0;
    for (const c of rows) {
      const delta = Math.abs(ts - new Date(c.created_at).getTime());
      if (delta <= MAX_DELTA_MS) {
        twilio += twilioCostUSD(c) * fx;
      }
    }
    return twilio;
  };

  // ---------- AVERAGES for AI Reply Generation calls (whole lookback window) ----------
  const avgTokens =
    generationMetrics.reduce((s, x) => s + (x.total_tokens || 0), 0) /
    (generationMetrics.length || 1);

  const avgOpenAICost =
    generationMetrics.reduce(
      (s, x) =>
        s +
        openaiCostUSD(x.model, x.prompt_tokens, x.completion_tokens) * fx,
      0
    ) / (generationMetrics.length || 1);

  const recentMetrics = generationMetrics.slice(0, TABLE_ROW_LIMIT);

  // ---------- COST PER CONVERSATION ----------
  const convCost = useMemo(() => {
    const map = {};
    const sorted = [...filteredConv].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    for (const row of sorted) {
      const u = row.utilisateur;
      if (!map[u]) map[u] = [];

      const list = map[u];
      if (list.length === 0) {
        list.push({
          start: row.created_at,
          end: row.created_at,
          twilio: twilioCostUSD(row) * fx,
          openai: 0,
          practitioner: row.praticien_id,
          user: u,
          messages: 1,
        });
      } else {
        const last = list[list.length - 1];
        const delta = new Date(row.created_at) - new Date(last.end);

        if (delta > 24 * 3600 * 1000) {
          list.push({
            start: row.created_at,
            end: row.created_at,
            twilio: twilioCostUSD(row) * fx,
            openai: 0,
            practitioner: row.praticien_id,
            user: u,
            messages: 1,
          });
        } else {
          last.end = row.created_at;
          last.twilio += twilioCostUSD(row) * fx;
          last.messages += 1;
        }
      }
    }

    // add OpenAI costs inside windows
    for (const m of filteredMetrics) {
      const list = map[m.utilisateur] || [];
      const ts = new Date(m.created_at);
      for (const c of list) {
        if (ts >= new Date(c.start) && ts <= new Date(c.end)) {
          c.openai +=
            openaiCostUSD(m.model, m.prompt_tokens, m.completion_tokens) * fx;
        }
      }
    }

    let all = Object.values(map).flat();

    if (monthFilter !== "all") {
      all = all.filter(
        (c) =>
          c.start.slice(0, 7) === monthFilter ||
          c.end.slice(0, 7) === monthFilter
      );
    }

    return all.sort((a, b) => new Date(b.start) - new Date(a.start));
  }, [filteredConv, filteredMetrics, fx, monthFilter]);

  const avgConvCost =
    convCost.reduce((s, c) => s + (c.twilio + c.openai), 0) /
    (convCost.length || 1);

  const avgMessages =
    convCost.reduce((s, c) => s + c.messages, 0) / (convCost.length || 1);

  const availableMonths = Array.from(
    new Set(conv.map((c) => c.created_at.slice(0, 7)))
  ).sort();

  const lookbackLabel =
    LOOKBACK_OPTIONS.find((o) => o.value === lookbackMonths)?.label ||
    `Last ${lookbackMonths} months`;

  return (
    <div className="space-y-10">
      {/* HEADER */}
      <div className="bg-white shadow rounded-xl p-6">
        <div className="flex flex-wrap items-center gap-4">
          <h1 className="text-2xl font-semibold">Cost Dashboard</h1>

          <select
            className="border px-3 py-2 rounded-lg"
            value={lookbackMonths}
            onChange={(e) => setLookbackMonths(Number(e.target.value))}
          >
            {LOOKBACK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <div className="ml-auto flex gap-4">
            <select
              className="border px-3 py-2 rounded-lg"
              value={selectedDoctor}
              onChange={(e) => setSelectedDoctor(e.target.value)}
            >
              <option value="all">All Practitioners</option>
              {practitionerList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm mt-4 font-semibold">
          <span>
            Total OpenAI Cost ({lookbackLabel}, all categories):{" "}
            {totalOpenAICostSgd.toFixed(3)} SGD
          </span>
        </div>

        <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm mt-2">
          <span>
            FX: 1 USD = {fx.toFixed(3)} SGD{" "}
            {fxIsLive ? "(live)" : "(fallback — live rate unavailable)"} —
            applied at today's rate to every period shown, not adjusted for
            the actual date of each charge.
          </span>
          <span>Avg Cost / Conversation: {avgConvCost.toFixed(3)} SGD</span>
          <span>Avg Messages / Conversation: {avgMessages.toFixed(1)}</span>
          {loading && <span className="text-gray-400">Loading…</span>}
        </div>

        <div className="mt-4 text-sm bg-sky-50 border border-sky-200 text-sky-900 rounded-lg p-3">
          Every OpenAI call site in the codebase now logs to openai_metrics
          (reply generation, classification, translation, RAG/embeddings,
          clinic digests, and the public demo endpoint) — see "Consumption
          by Category" below for the full picture, not just AI replies.
        </div>

        {Object.keys(unpricedModels).length > 0 && (
          <div className="mt-2 text-sm bg-red-50 border border-red-200 text-red-900 rounded-lg p-3">
            No pricing configured for:{" "}
            {Object.entries(unpricedModels)
              .map(([model, count]) => `${model} (${count} calls)`)
              .join(", ")}
            . These are excluded from every cost total below until
            MODEL_PRICES is updated — treat all figures here as
            under-counted while this warning shows.
          </div>
        )}

        {(truncated.metrics || truncated.conv) && (
          <div className="mt-2 text-sm bg-red-50 border border-red-200 text-red-900 rounded-lg p-3">
            More than {FETCH_SAFETY_LIMIT.toLocaleString()} rows exist in
            this window for{" "}
            {[
              truncated.metrics && "OpenAI calls",
              truncated.conv && "conversations",
            ]
              .filter(Boolean)
              .join(" and ")}
            . Data has been truncated to the most recent{" "}
            {FETCH_SAFETY_LIMIT.toLocaleString()} rows — narrow the lookback
            window for complete totals.
          </div>
        )}
      </div>

      {/* CONSUMPTION BY CATEGORY */}
      <div className="bg-white shadow rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-1">
          Consumption by Category ({lookbackLabel})
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Every OpenAI call in the app falls into one of these categories —
          this is the actual total, not just AI replies.
        </p>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Category</th>
              <th className="p-2">Calls</th>
              <th className="p-2">Tokens</th>
              <th className="p-2">Cost (SGD)</th>
              <th className="p-2">% of Total</th>
            </tr>
          </thead>
          <tbody>
            {costByGroup.map((g) => (
              <tr key={g.group} className="border-b">
                <td className="p-2 text-left">{g.group}</td>
                <td className="p-2">{g.calls.toLocaleString()}</td>
                <td className="p-2">{g.tokens.toLocaleString()}</td>
                <td className="p-2">{g.cost.toFixed(3)}</td>
                <td className="p-2">
                  {totalOpenAICostSgd > 0
                    ? `${((g.cost / totalOpenAICostSgd) * 100).toFixed(1)}%`
                    : "—"}
                </td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="p-2 text-left">Total</td>
              <td className="p-2">
                {costByCallType.reduce((s, r) => s + r.calls, 0).toLocaleString()}
              </td>
              <td className="p-2">
                {costByCallType.reduce((s, r) => s + r.tokens, 0).toLocaleString()}
              </td>
              <td className="p-2">{totalOpenAICostSgd.toFixed(3)}</td>
              <td className="p-2">100%</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* MONTHLY */}
      <div className="bg-white shadow rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">
          Monthly Total Cost ({lookbackLabel})
        </h2>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthly}>
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="total" fill="#14b8a6" name="Total" />
            <Bar dataKey="openai" fill="#0ea5e9" name="OpenAI" />
            <Bar dataKey="twilio" fill="#f97316" name="Twilio" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* MODEL COST */}
      <div className="bg-white shadow rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">
          OpenAI Cost by Model ({lookbackLabel})
        </h2>

        <ul className="space-y-2">
          {Object.entries(costByModel).map(([model, cost]) => (
            <li key={model} className="flex justify-between border-b pb-1">
              <span>
                {model}
                {!isModelPriced(model) && (
                  <span className="text-red-600 ml-2">(unpriced)</span>
                )}
              </span>
              <span>{cost.toFixed(3)} SGD</span>
            </li>
          ))}
          <li className="flex justify-between font-semibold pt-2">
            <span>Total</span>
            <span>
              {Object.values(costByModel)
                .reduce((a, b) => a + b, 0)
                .toFixed(3)}{" "}
              SGD
            </span>
          </li>
        </ul>
      </div>

      {/* DETAIL BY CALL TYPE */}
      <div className="bg-white shadow rounded-xl p-6 overflow-auto">
        <h2 className="text-xl font-semibold mb-1">
          Detail by Call Type ({lookbackLabel})
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Every distinct kind of OpenAI call, across all categories —
          this is the finest-grained breakdown available.
        </p>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Call Type</th>
              <th className="p-2 text-left">Category</th>
              <th className="p-2">Calls</th>
              <th className="p-2">Tokens</th>
              <th className="p-2">Cost (SGD)</th>
            </tr>
          </thead>
          <tbody>
            {costByCallType.map((row) => (
              <tr key={row.call_type} className="border-b">
                <td className="p-2 text-left">
                  {callTypeMeta(row.call_type).label}
                </td>
                <td className="p-2 text-left text-gray-500">
                  {callTypeMeta(row.call_type).group}
                </td>
                <td className="p-2">{row.calls.toLocaleString()}</td>
                <td className="p-2">{row.tokens.toLocaleString()}</td>
                <td className="p-2">{row.cost.toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* LAST GPT CALLS */}
      <div className="bg-white shadow rounded-xl p-6 overflow-auto">
        <h2 className="text-xl font-semibold mb-1">
          Last {recentMetrics.length} of {generationMetrics.length} AI Reply
          Generation Calls
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Twilio column is an estimate: it matches WhatsApp messages from the
          same patient within 10 seconds of the AI reply, not a real ledger
          link.
        </p>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2">Date</th>
              <th className="p-2">Practitioner</th>
              <th className="p-2">Patient</th>
              <th className="p-2">Model</th>
              <th className="p-2">Prompt</th>
              <th className="p-2">Completion</th>
              <th className="p-2">Total</th>
              <th className="p-2">OpenAI</th>
              <th className="p-2">Twilio (≈)</th>
              <th className="p-2">Total Cost</th>
            </tr>
          </thead>

          <tbody>
            {recentMetrics.map((m) => {
              const openaiSgd =
                openaiCostUSD(m.model, m.prompt_tokens, m.completion_tokens) *
                fx;
              const twi = associateTwilioToOpenAI(m);

              return (
                <tr key={m.id} className="border-b">
                  <td className="p-2">{formatSG(m.created_at)}</td>
                  <td className="p-2">
                    {clients.find((c) => c.id === m.praticien_id)
                      ?.raison_sociale || "—"}
                  </td>
                  <td className="p-2">
                    {m.utilisateur?.replace("whatsapp:", "")}
                  </td>
                  <td className="p-2">
                    {m.model}
                    {!isModelPriced(m.model) && (
                      <span className="text-red-600 ml-1">*</span>
                    )}
                  </td>
                  <td className="p-2">{m.prompt_tokens}</td>
                  <td className="p-2">{m.completion_tokens}</td>
                  <td className="p-2">{m.total_tokens}</td>
                  <td className="p-2">{openaiSgd.toFixed(4)}</td>
                  <td className="p-2">{twi.toFixed(4)}</td>
                  <td className="p-2 font-semibold">
                    {(openaiSgd + twi).toFixed(4)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-4 text-sm text-gray-700">
          <div>Average Tokens ({lookbackLabel}): {avgTokens.toFixed(1)}</div>
          <div>
            Average OpenAI Cost ({lookbackLabel}): {avgOpenAICost.toFixed(4)}{" "}
            SGD
          </div>
        </div>
      </div>

      {/* OTHER AI CALLS */}
      <div className="bg-white shadow rounded-xl p-6 overflow-auto">
        <h2 className="text-xl font-semibold mb-1">
          Last {recentOtherCalls.length} of {otherMetrics.length} Other AI
          Calls
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Everything that isn't a final AI reply — classification,
          translation, RAG/embeddings, clinic digests, demo. No patient/Twilio
          columns here: most of these calls aren't tied to one WhatsApp
          message.
        </p>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2">Date</th>
              <th className="p-2">Practitioner</th>
              <th className="p-2 text-left">Call Type</th>
              <th className="p-2">Model</th>
              <th className="p-2">Prompt</th>
              <th className="p-2">Completion</th>
              <th className="p-2">Total</th>
              <th className="p-2">Cost (SGD)</th>
            </tr>
          </thead>
          <tbody>
            {recentOtherCalls.map((m) => {
              const openaiSgd =
                openaiCostUSD(m.model, m.prompt_tokens, m.completion_tokens) *
                fx;
              return (
                <tr key={m.id} className="border-b">
                  <td className="p-2">{formatSG(m.created_at)}</td>
                  <td className="p-2">
                    {clients.find((c) => c.id === m.praticien_id)
                      ?.raison_sociale || "—"}
                  </td>
                  <td className="p-2 text-left">
                    {callTypeMeta(m.call_type).label}
                  </td>
                  <td className="p-2">
                    {m.model}
                    {!isModelPriced(m.model) && (
                      <span className="text-red-600 ml-1">*</span>
                    )}
                  </td>
                  <td className="p-2">{m.prompt_tokens}</td>
                  <td className="p-2">{m.completion_tokens}</td>
                  <td className="p-2">{m.total_tokens}</td>
                  <td className="p-2 font-semibold">{openaiSgd.toFixed(4)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* COST PER CONVERSATION */}
      <div className="bg-white shadow rounded-xl p-6 overflow-auto">
        <div className="flex items-center gap-4 mb-4">
          <h2 className="text-xl font-semibold">Cost per Conversation</h2>

          <select
            className="border px-3 py-2 rounded-lg ml-auto"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          >
            <option value="all">All Months</option>
            {availableMonths.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2">Start</th>
              <th className="p-2">End</th>
              <th className="p-2">Practitioner</th>
              <th className="p-2">Messages</th>
              <th className="p-2">Twilio</th>
              <th className="p-2">OpenAI</th>
              <th className="p-2">Total</th>
            </tr>
          </thead>

          <tbody>
            {convCost.map((c, i) => (
              <tr key={i} className="border-b">
                <td className="p-2">{formatSG(c.start)}</td>
                <td className="p-2">{formatSG(c.end)}</td>
                <td className="p-2">
                  {clients.find((x) => x.id === c.practitioner)
                    ?.raison_sociale || "—"}
                </td>
                <td className="p-2">{c.messages}</td>
                <td className="p-2">{c.twilio.toFixed(3)}</td>
                <td className="p-2">{c.openai.toFixed(3)}</td>
                <td className="p-2 font-semibold">
                  {(c.twilio + c.openai).toFixed(3)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 text-sm font-semibold">
          Average Cost per Conversation: {avgConvCost.toFixed(3)} SGD
          <br />
          Average Messages per Conversation: {avgMessages.toFixed(1)}
        </div>
      </div>
    </div>
  );
}
